import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './BiddingEnabledFilter.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Map UI value -> URL query param value
// - all -> remove filter
// - bidding -> true
const format = (selectedOption, queryParamName) => {
  const value = selectedOption === 'bidding' ? 'true' : null;
  return { [queryParamName]: value };
};

const IconCheckbox = props => {
  const { checked } = props;
  return (
    <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg" role="presentation">
      <g fill="none" fillRule="evenodd">
        <g transform="translate(2 2)">
          <rect
            className={checked ? css.checked : css.notChecked}
            x="0"
            y="0"
            width="10"
            height="10"
            rx="2"
          />
          <rect className={css.box} x="-1" y="-1" width="12" height="12" rx="2" fill="none" strokeWidth="1" />
        </g>
        <path
          className={checked ? css.tick : css.tickHidden}
          d="M6.42605 11.0758C6.10105 11.4008 5.57604 11.4008 5.25104 11.0758L2.25937 8.08411C1.93438 7.75911 1.93438 7.23411 2.25937 6.90911C2.58437 6.58411 3.10937 6.58411 3.43437 6.90911L5.83438 9.30911L11.5677 3.57578C11.8927 3.25078 12.4177 3.25078 12.7427 3.57578C13.0677 3.90078 13.0677 4.42578 12.7427 4.75078L6.42605 11.0758Z"
          fill="white"
        />
      </g>
    </svg>
  );
};

/**
 * BiddingEnabledFilter
 *
 * A 2-option listing-type filter:
 * - All listings
 * - Bidding
 *
 * It maps to `pub_biddingEnabled=true` and omits the param for "all".
 * The UI is intentionally styled to match SelectMultipleFilter (e.g. Product Type).
 */
const BiddingEnabledFilter = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const rawInit = initialValues?.[queryParamName];
  const selectedOption = rawInit === true || rawInit === 'true' ? 'bidding' : 'all';

  const labelForPopup = selectedOption === 'bidding' ? label : label;

  const namedInitialValues = { [name]: selectedOption };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName));
  };

  const Options = ({ className }) => (
    <div className={className}>
      <ul className={css.list}>
        {options.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <Field name={name} type="radio" value={option}>
                {({ input }) => (
                  <>
                    <input id={fieldId} className={css.input} {...input} />
                    <label htmlFor={fieldId} className={css.label}>
                      <span className={css.optionIcon}>
                        <IconCheckbox checked={!!input.checked} />
                      </span>
                      <span className={css.optionText}>{label}</span>
                    </label>
                  </>
                )}
              </Field>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      ariaLabel={getAriaLabel(label, selectedOption === 'bidding' ? 'Bidding' : '')}
      isSelected={selectedOption === 'bidding'}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <Options className={css.fieldGroup} />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      ariaLabel={getAriaLabel(label, selectedOption === 'bidding' ? 'Bidding' : '')}
      isSelected={selectedOption === 'bidding'}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <Options className={css.fieldGroupPlain} />
    </FilterPlain>
  );
};

export default BiddingEnabledFilter;
